import { Globe } from 'lucide-react';

import { LANGUAGE_NAMES, LANGUAGES, useLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * The language switcher.
 *
 * A row of three buttons rather than a `<select>`: with three options the menu
 * costs a click and hides two of them, and each option is written in its own
 * language — someone who has landed in the wrong one can still read their way
 * out.
 */
export function LanguagePicker({
  label,
  onChange,
  className,
}: {
  label?: string;
  /** Called after the switch, so a caller can persist it to the account. */
  onChange?: (language: (typeof LANGUAGES)[number]) => void;
  className?: string;
}) {
  const [language, setLanguage] = useLanguage();

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {label ? (
        <span className="flex items-center gap-1.5 text-xs text-content-subtle">
          <Globe className="h-3.5 w-3.5" aria-hidden />
          {label}
        </span>
      ) : null}
      <div
        role="radiogroup"
        aria-label={label ?? 'Language'}
        className="flex gap-1 rounded-md bg-surface-sunken p-0.5"
      >
        {LANGUAGES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === language}
            lang={option}
            onClick={() => {
              setLanguage(option);
              onChange?.(option);
            }}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium transition-colors',
              option === language
                ? 'bg-surface text-content shadow-sm'
                : 'text-content-muted hover:text-content',
            )}
          >
            {LANGUAGE_NAMES[option]}
          </button>
        ))}
      </div>
    </div>
  );
}
